/**
 * Job Hunter — one CV full screen: the file (or its text) on the left, the AI
 * health review on the right. The text is what the agents read; editing it
 * clears the review.
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ExternalLink, FileText, LoaderCircle, Save, Star, X } from 'lucide-react';
import { Button } from '@/components/Button';
import { jobHunterService, type JhCv, type JhCvHealth, type JhProfile } from '@/services/job-hunter.service';
import { fmtBytes, fmtDate } from '@/lib/job-hunter/format';
import { Pill, Segmented } from './ui';
import { CvHealthPanel } from './CvHealthPanel';

export type CvDetailTab = 'file' | 'text';

export function CvDetail({ cvUid, profile, initialTab = 'file', autoAnalyze, onClose, onChanged }: {
  cvUid: string | null;
  profile: JhProfile | null;
  initialTab?: CvDetailTab;
  autoAnalyze?: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [cv, setCv] = useState<JhCv | null>(null);
  const [tab, setTab] = useState<CvDetailTab>(initialTab);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cvUid) { setCv(null); return; }
    let live = true;
    setCv(null);
    jobHunterService.cv(cvUid).then((c) => {
      if (!live) return;
      setCv(c); setText(c.text ?? '');
      setTab(initialTab === 'file' && !c.file ? 'text' : initialTab);
    }).catch(() => { if (live) onClose(); });
    return () => { live = false; };
  }, [cvUid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cvUid) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [cvUid, onClose]);

  if (!cvUid) return null;
  const dirty = !!cv && text !== (cv.text ?? '');
  const isPdf = cv?.file?.mime === 'application/pdf';

  const saveText = async () => {
    if (!cv) return;
    if (cv.health && !window.confirm('Saving new text clears this CV’s health review (it was measured on the old text). Continue?')) return;
    setSaving(true);
    try {
      await jobHunterService.updateCv(cv.cvUid, { text });
      const fresh = await jobHunterService.cv(cv.cvUid);
      setCv(fresh); setText(fresh.text ?? '');
      toast.success('CV text saved — the agents use it from their next scoring');
      onChanged();
    } catch { /* toasted */ } finally { setSaving(false); }
  };

  return (
    <>
      <div className="jh-drawer-backdrop" onClick={onClose} />
      <div className="jh-sheet" role="dialog" aria-modal="true" aria-label={cv?.label ?? 'CV'}>
        <div className="flex flex-wrap items-center gap-3 border-b border-border bg-surface px-4 py-3 sm:px-5">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-danger-soft text-danger"><FileText size={18} /></span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-[16px] font-bold tracking-tight">{cv?.label ?? 'Loading…'}</h2>
              {cv?.isDefault && <Pill tone="warning" icon={<Star size={11} />}>Default</Pill>}
            </div>
            {cv && <div className="mt-0.5 truncate text-[12px] text-text-muted">{cv.file ? `${cv.file.name} · ${cv.file.pages ? `${cv.file.pages} page${cv.file.pages === 1 ? '' : 's'} · ` : ''}${fmtBytes(cv.file.size)}` : 'Pasted text'} · added {fmtDate(cv.createdAt)}</div>}
          </div>
          {cv?.file?.url && <a href={cv.file.url} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" iconRight={<ExternalLink size={13} />}>Open file</Button></a>}
          <button type="button" onClick={onClose} aria-label="Close" className="grid h-9 w-9 place-items-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary"><X size={18} /></button>
        </div>

        {!cv ? <div className="grid flex-1 place-items-center"><LoaderCircle className="animate-spin text-primary" size={26} /></div> : (
          <div className="grid min-h-0 flex-1 overflow-y-auto lg:grid-rows-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:overflow-hidden">
            <div className="jh-sheet-col flex flex-col border-b border-border p-4 lg:border-b-0 lg:border-r sm:p-5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <Segmented<CvDetailTab> value={tab} onChange={setTab} options={[{ value: 'file', label: 'File' }, { value: 'text', label: `Text the agents read${dirty ? ' •' : ''}` }]} />
                {tab === 'text' && <Button size="sm" loading={saving} disabled={!dirty} iconLeft={<Save size={13} />} onClick={saveText}>Save text</Button>}
              </div>
              {tab === 'file' ? (
                cv.file?.url ? (
                  isPdf
                    // <object> falls back to its children where the browser has no PDF viewer (an iframe just shows a black box).
                    ? (
                      <object data={`${cv.file.url}#view=FitH`} type="application/pdf" aria-label={cv.file.name} className="min-h-[60vh] w-full flex-1 rounded-xl border border-border bg-white">
                        <div className="grid min-h-[60vh] place-items-center p-6 text-center">
                          <div>
                            <p className="jh-muted">This browser can't show PDFs inline.</p>
                            <div className="mt-3 flex justify-center gap-2">
                              <a className="jh-chip hover:bg-primary-soft hover:text-primary" href={cv.file.url} target="_blank" rel="noreferrer">Open the PDF</a>
                              <button type="button" className="jh-chip hover:bg-primary-soft hover:text-primary" onClick={() => setTab('text')}>Show the text</button>
                            </div>
                          </div>
                        </div>
                      </object>
                    )
                    : <pre className="min-h-[60vh] flex-1 overflow-auto whitespace-pre-wrap rounded-xl border border-border bg-surface p-4 text-[12.5px] leading-relaxed">{cv.text}</pre>
                ) : <div className="grid min-h-[40vh] flex-1 place-items-center rounded-xl border border-dashed border-border p-6 text-center jh-muted">This CV was pasted as text — there is no file. Upload a PDF to replace it from the library.</div>
              ) : (
                <div className="flex flex-1 flex-col">
                  <textarea className="jh-input min-h-[60vh] flex-1 font-mono text-[12.5px]" value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} placeholder="Paste the CV text here…" />
                  <p className="jh-field-hint">This is exactly what the agents score jobs against and draft from. {cv.textSource === 'file' ? 'It came out of the PDF.' : cv.textSource === 'edited' ? 'Edited by hand.' : 'Pasted by hand.'} Saving clears the health review.</p>
                </div>
              )}
            </div>
            <div className="jh-sheet-col p-4 sm:p-5">
              <CvHealthPanel key={cv.cvUid} cv={cv} profile={profile} autoRun={autoAnalyze && !dirty}
                onAnalyzed={(h: JhCvHealth) => { setCv((c) => (c ? { ...c, health: h, demand: h.demand ?? c.demand } : c)); onChanged(); }} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
